import { Component, Input, Output, EventEmitter, OnInit } from '@angular/core';
import * as _ from 'lodash';
import { AdminService } from '../../../../services/core.admin.service';
import { MyReportsFilterService } from '../services/myreports.service.filters';
import { MyReportsRest } from '../services/myreports.service';
import { UiService } from '../../../../services/core.ui.service';
import { GfTranslateService } from '../../../../services/core.translate.service';
import { UserRest } from '../../../../services/core.user.rest.service';
import { UserService } from '../../../../services/core.user.service';
import { StateService } from '@uirouter/angular';

@Component({
  selector: 'my-reports-filter',
  template: require('./myreports.filter.component.html')
})
export class MyReportsFilterComponent implements OnInit {
  @Input() siteConfig: any;
  @Input() user: any;
  @Input() myReport: any;

  @Output() onSave: EventEmitter<any> = new EventEmitter<any>();

  filterCriteria: any = false;
  displayFields: any = false;
  filterGroup: string = 'projects-trackers';
  filterTracker: string = '';
  showSelectedProjects: boolean = false;
  selectFilters: any = {};
  SharedChoices: any = { 'Private': false, 'Shared': true };

  maxTrackersDefault: number = 50;
  maxTrackers: number;
  searchAssignee: string;
  searchStatus: string = '';
  searchPriority: string = '';
  results: any;
  
  // Filtered report settings properties
  filteredStatuses: any[] = [];
  filteredAssignees: any[] = [];
  filteredPriorities: any[] = [];
  filteredSelectElements: any = {};
  orderedDisplayFields: any[] = [];

  constructor(
    private adminService: AdminService,
    public myReportsFilterService: MyReportsFilterService,
    private myReportsRest: MyReportsRest,
    private uiService: UiService,
    private userRest: UserRest,
    private stateService: StateService, 
    
    public userService: UserService,
    private translateService: GfTranslateService
  ) {
   }

  ngOnInit() {
    // Set maximum trackers default from configuration if available.
    this.maxTrackersDefault = _.get(this.siteConfig, 'myReportsMaxTrackersCount', this.maxTrackersDefault);
    let assignees: any[] = [];
    const promises: Promise<any>[] = [];

    // If it's an "all trackers" report type, fetch assignees for the report.
    if (this.myReport.reportType === 1) {
      const assigneesFilter = _.find(this.myReport.fieldFilters, { fieldName: 'tracker_item_assignee' });
      let assigneeFilterIds = '';
      if (assigneesFilter) {
        assigneeFilterIds = _.map(assigneesFilter.values, (a: any) => a.value).join(',');
      }
      if (assigneeFilterIds !== '') {
        promises.push(
          this.userRest.getBasic('', { ids: assigneeFilterIds })
            .then((response: any) => {
              assignees = _.map(response.items, (assignee: any) => ({
                id: assignee.id,
                label: this.userService.fullName(assignee),
                enabled: true,
                selected: true
              }));
            })
            .catch(() => { /* ignore error */ })
        );
      }
    }

    promises.push(
      this.adminService.getConfig("config", 0, "myreports.maxtrackers", this.maxTrackersDefault)
        .then(setting => this.setMaxTrackers(setting), () => { /* no-op */ })
    );

    Promise.all(promises)
      .finally(() => {
        this.checkMaxTrackers();
        this.setupReportCriteria(assignees);
      });
  }

  setConfigValue(path: string, value: any): boolean {
    if (value === undefined || value === _.get(this, path)) return false;
    _.set(this, path, value);
    return true;
  }

  showProject(p: any, filter: string, showSelectedProjects: boolean): boolean {
    if (filter !== '') {
      // Replace AngularJS filter with lodash filtering.
      const matches = _.filter(p.trackers, t => t.label.indexOf(filter) !== -1);
      if (matches.length === 0) return false;
    }
    if (showSelectedProjects) {
      return _.filter(p.trackers, { selected: true }).length > 0;
    }
    return true;
  }

  getSectionFiltersCount(section: string): number {
    return this.myReportsFilterService.countUsedFilters(this.filterCriteria, section);
  }

  toggleSelectedTracker(project_id: string, tracker_id: string): Promise<any> {
    const tracker = this.myReportsFilterService.getTrackerFromFilters(this.filterCriteria, project_id, tracker_id);
    return new Promise((resolve, reject) => {
      if (!_.isEmpty(tracker) && !tracker.selected) {
        if (this.filterCriteria.trackers.count >= this.maxTrackers) {
          this.uiService.message.showError(
            this.translateService.translate('You already selected the maximum allowed number of trackers ({{maxTrackers}}) per report', { maxTrackers: this.maxTrackers }),
            this.translateService.translate('Trackers limit exceeded')
          );
          resolve(null);
          return;
        }
        this.myReportsFilterService.getBaseFilters([tracker.id])
          .then(data => {
            this.myReportsFilterService.setFilterAssignees(this.filterCriteria.assignees, data.assigneesByTracker);
            this.myReportsFilterService.setFilterElements(this.filterCriteria.statuses, data.statusesByTracker);
            resolve(null);
          });
      } else {
        resolve(null);
      }
    }).then(() => {
      this.myReportsFilterService.toggleSelectedTracker(this.filterCriteria, project_id, tracker_id);
      const trackerIds = this.myReportsFilterService.getSelectedTrackerIds(this.filterCriteria.trackers.data);
      const trackerParam = trackerIds.length === 1 ? trackerIds[0] : null;
      return this.myReportsFilterService.setTrackerExtraFields(this.filterCriteria, trackerParam)
        .then(() => {
          this.displayFields = this.myReportsFilterService.getDisplayFields(this.filterCriteria);
        });
    });
  }

  toggleSelectedElement(group_id: string, subgroup_id: string, element_id: string): void {
    this.myReportsFilterService.toggleSelectedElement(this.filterCriteria, group_id, subgroup_id, element_id);
  }

  moveField(f: any, e: Event, up: boolean): void {
    this.displayFields = this.myReportsFilterService.changeFieldOrder(f, this.displayFields, up);
    this.updateOrderedDisplayFields(); // Update cached ordered fields
    e.stopPropagation();
  }

  selectAllProjects(unsetTrackers?: boolean): void {
    this.myReport.reportType = unsetTrackers ? 0 : 1;
    _.forEach(this.filterCriteria.trackers.data, (project: any) => {
      _.forEach(project.trackers, (tracker: any) => {
        tracker.selected = false;
      });
    });
    this.filterCriteria.trackers.count = 0;
    this.filterCriteria.statuses.data = this.myReportsFilterService.getDefaultStatuses();
    this.filterCriteria.statuses.count = 0;
  }

  selectAllElements(section: string, flag: boolean, subgroup?: string): void {
    subgroup = subgroup || null;
    const elements = this.myReportsFilterService.getElements(this.filterCriteria, section, subgroup);
    _.forEach(elements, (e: any) => {
      if (_.get(e, 'enabled', true) && e.selected !== flag) {
        this.myReportsFilterService.toggleSelectedElement(this.filterCriteria, section, subgroup, e.label);
        e.selected = flag; // (As in the original code.)
      }
    });
    this.myReportsFilterService.updateSelectedCount(this.filterCriteria, section);
  }

  save(): void {
    if (!this.validateReportSettings()) {
      this.uiService.message.showError(
        this.translateService.translate("Reports must have a name and at least one tracker selected"),
        this.translateService.translate("Save Report")
      );
      return;
    }
    this.myReport = this.myReportsFilterService.mapFiltersToReportSettings(this.myReport, this.filterCriteria);
    this.myReportsRest.save(this.myReport.id, this.myReport)
      .then(response => {
        if (this.myReport.id === 0) {
          this.myReport = response;
          const params = this.stateService.params;
          params.id = this.myReport.id;
          this.stateService.go("home.user.myreports.report", params, { reload: true });
        } else {
          this.onSave.emit();
        }
        this.uiService.message.showSuccess(
          this.translateService.translate("Report saved successfully"),
          this.translateService.translate("Save Successful")
        );
      })
      .catch(err => {
        this.uiService.message.showError(
          this.translateService.translate("There was an error, there report could not be saved"),
          this.translateService.translate("Save Error")
        );
      });
  }

  getFilterWarningClass(reportType: number, count: number, max: number): string {
    if (reportType === 1) {
      return 'label-success';
    }
    if (count >= max) {
      return 'label-danger';
    }
    if (count > (max - 10)) {
      return 'label-warning';
    }
    return 'label-default';
  }

  doSearchAssignee(): void {
    if (this.myReport.reportType !== 1) return;
    if (this.searchAssignee === '') {
      this.filterCriteria.assignees.data = _.filter(this.filterCriteria.assignees.data, { selected: true });
      this.updateFilteredAssignees();
      return;
    }
    const params = {
      text: this.searchAssignee,
      sort_field: 'lastname',
      sort_dir: 'asc',
      page_num: 1,
      page_size: 10
    };
    let newAssignees: any[] = [];
    this.userRest.getBasic('', params)
      .then((response: any) => {
        newAssignees = _.map(response.items, (assignee: any) => ({
          id: assignee.id,
          label: this.userService.fullName(assignee) + " (" + assignee.unixName + ")",
          enabled: true,
          selected: false
        }));
        this.filterCriteria.assignees.data = _.unionBy(
          _.filter(this.filterCriteria.assignees.data, { selected: true }),
          newAssignees,
          'id'
        );
        this.updateFilteredAssignees();
      })
      .catch(() => {
        this.results = [];
        this.updateFilteredAssignees();
      });
  }

  onOpenDateStartUpdate($event: any): void {
    this.filterCriteria.basic.openDateStart = $event;
  }

  onOpenDateEndUpdate($event: any): void {
    this.filterCriteria.basic.openDateEnd = $event;
  }

  onCloseDateStartUpdate($event: any): void {
    this.filterCriteria.basic.closeDateStart = $event;
  }

  onCloseDateEndUpdate($event: any): void {
    this.filterCriteria.basic.closeDateEnd = $event;
  }

  onDateFilterFromUpdate($event: any, dateObj: any): void {
    dateObj.from = $event;
  }

  onDateFilterToUpdate($event: any, dateObj: any): void {
    dateObj.to = $event;
  }

  updateFilteredStatuses(): void {
    if (!this.filterCriteria?.statuses?.data) {
      this.filteredStatuses = [];
      return;
    }
    if (!this.searchStatus) {
      this.filteredStatuses = this.filterCriteria.statuses.data;
      return;
    }
    
    this.filteredStatuses = this.filterCriteria.statuses.data.filter(status =>
      status.label.toLowerCase().includes(this.searchStatus.toLowerCase())
    );
  }

  updateFilteredAssignees(): void {
    if (!this.filterCriteria?.assignees?.data) {
      this.filteredAssignees = [];
      return;
    }
    if (!this.searchAssignee) {
      this.filteredAssignees = this.filterCriteria.assignees.data;
      return;
    }
    
    this.filteredAssignees = this.filterCriteria.assignees.data.filter(assignee =>
      assignee.label.toLowerCase().includes(this.searchAssignee.toLowerCase())
    );
  }

  updateFilteredPriorities(): void {
    if (!this.filterCriteria?.priorities?.data) {
      this.filteredPriorities = [];
      return;
    }
    if (!this.searchPriority) {
      this.filteredPriorities = this.filterCriteria.priorities.data;
      return;
    }
    
    this.filteredPriorities = this.filterCriteria.priorities.data.filter(priority =>
      priority.label.toLowerCase().includes(this.searchPriority.toLowerCase())
    );
  }

  updateFilteredSelectElements(): void {
    this.filteredSelectElements = {};
    
    if (!this.filterCriteria?.selects) return;
    
    const allSelects = (this.filterCriteria.selects.basic || []).concat(this.filterCriteria.selects.extra || []);
    
    allSelects.forEach(selectFilter => {
      const filterValue = this.selectFilters[selectFilter.label];
      if (!filterValue) {
        this.filteredSelectElements[selectFilter.label] = selectFilter.elements || [];
      } else {
        this.filteredSelectElements[selectFilter.label] = (selectFilter.elements || []).filter(element =>
          element.label.toLowerCase().includes(filterValue.toLowerCase())
        );
      }
    });
  }

  updateOrderedDisplayFields(): void {
    if (!this.displayFields) {
      this.orderedDisplayFields = [];
      return;
    }
    this.orderedDisplayFields = [...this.displayFields].sort((a, b) => {
      return (a.columnOrder || 0) - (b.columnOrder || 0);
    });
  }

  getFilteredTrackers(trackers: any[]): any[] {
    if (!trackers) return [];
    if (!this.filterTracker) return trackers;
    
    return trackers.filter(tracker => 
      tracker.label.toLowerCase().includes(this.filterTracker.toLowerCase())
    );
  }

  private validateReportSettings(): boolean {
    return (!_.isEmpty(this.filterCriteria.basic.reportName) &&
      (this.filterCriteria.trackers.count > 0 || this.myReport.reportType === 1));
  }

  private setupReportCriteria(assignees: any[]): void {
    const trackerIds = _.map(_.get(this.myReport, 'trackers', {}), (tracker: any) => tracker.id);
    Promise.all([
      this.myReportsFilterService.getProjects(_.get(this.user, 'unixName', 'None')),
      this.myReportsFilterService.getBaseFilters(trackerIds)
    ]).then(([projects, baseFilters]) => {
      const params = {
        trackers: _.get(projects, 'trackersByProject', {}),
        assignees: _.get(baseFilters, 'assigneesByTracker', {}),
        statuses: _.get(baseFilters, 'statusesByTracker', {})
      };
      this.myReportsFilterService.mapReportSettingsToFilters(
        this.myReportsFilterService.init(params),
        this.myReport
      ).then(filters => {
        this.filterCriteria = filters;
        this.displayFields = this.myReportsFilterService.getDisplayFields(this.filterCriteria);
        if (assignees?.length > 0 && Array.isArray(assignees)) {
          this.filterCriteria.assignees.data = _.unionBy(assignees, this.filterCriteria.assignees.data, 'id');
          this.filterCriteria.assignees.count = _.filter(this.filterCriteria.assignees.data, { selected: true }).length;
        }
        
        // Initialize cached filtered properties
        this.updateFilteredStatuses();
        this.updateFilteredAssignees();
        this.updateFilteredPriorities();
        this.updateFilteredSelectElements();
        this.updateOrderedDisplayFields();
      });
    }).catch(response => {
      this.uiService.message.showError(
        this.translateService.translate("Could not fetch the report criteria"),
        this.translateService.translate("Error")
      );
    });
  }

  private setMaxTrackers(setting: any): void {
    this.maxTrackers = _.max([_.get(setting, 'settingValue', 0), this.maxTrackersDefault]);
  }

  private checkMaxTrackers(): void {
    if (this.myReport.reportType !== 1) {
      const selectedTrackersCount = _.get(this.myReport, 'trackers.length', 0);
      if (selectedTrackersCount > this.maxTrackers) {
        this.uiService.message.showError(
          this.translateService.translate("Your report contains {{selectedTrackersCount}} selected trackers, which exceeds the current limit of {{maxTrackers}}",
            { selectedTrackersCount: selectedTrackersCount, maxTrackers: this.maxTrackers }
          ),
          this.translateService.translate('Trackers limit exceeded')
        );
      }
    }
  }

  trackById(index: number, item: any): any {
    return item?.id ?? item;
  }
}
